import { X } from 'lucide-react';
import { useFleet } from '@/store/fleet';
import { useMapMode } from '@/store/mapMode';
import { useMeasure } from '@/store/measure';
import { useSelection } from '@/store/selection';
import { distMeters } from '@/store/gotoTargets';
import { fmtMeters } from '@/lib/utils';

/** Initial bearing (degrees, 0–360) from `from` to `to`. */
function bearingDeg(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lon - from.lon) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Measure-mode readout — slim bottom-centre strip showing the live distance
 * and bearing from the selected robot to the clicked range point. Distance
 * updates as the robot moves (it reads live fleet state each render).
 */
export function MeasureOverlay() {
  const mode = useMapMode((s) => s.mode);
  const point = useMeasure((s) => s.point);
  const clear = useMeasure((s) => s.clear);
  const selected = useSelection((s) => s.selected);
  const robotId = selected.size > 0 ? (Array.from(selected)[0] as string) : null;
  const robot = useFleet((s) => (robotId ? s.robots[robotId] : undefined));
  const state = useFleet((s) => (robotId ? s.states[robotId] : undefined));

  if (mode !== 'measure') return null;

  const pos = state?.position;
  const dist = point && pos ? distMeters(pos, point) : null;
  const brg = point && pos ? bearingDeg(pos, point) : null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center px-3">
      <div className="pointer-events-auto flex items-center gap-3 rounded-sm border border-border-default bg-base/85 px-3 py-1.5 font-mono text-[10.5px] text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur-md">
        {!robotId ? (
          <span>Select a drone, then click the map to measure</span>
        ) : !point ? (
          <span>
            Click the map — distance from{' '}
            <span className="text-fg-primary">{robot?.name ?? robotId}</span>
          </span>
        ) : (
          <>
            <span className="font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--color-accent)' }}>
              Range
            </span>
            <span className="text-fg-primary">
              {dist != null ? fmtMeters(dist) : '— (no position)'}
            </span>
            {brg != null && <span>BRG {String(Math.round(brg)).padStart(3, '0')}°</span>}
            <span className="text-fg-muted">
              {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
            </span>
            <button
              type="button"
              onClick={clear}
              title="Clear range point"
              className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-primary"
            >
              <X className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
